import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon } from 'components/icons';
import Skeleton from '../skeleton/Skeleton';
import { formatLocalTime } from 'utils/formatDate';
import cx from 'classnames';
import { useAppDispatch } from 'store/hooks';
import { setRewardsOpen } from 'store/uiSlice';
import useBonus from 'hooks/useBonus';
import styles from './spinDetails.module.scss';

interface LevelBonusDetailsProps {
  details: any;
  onBack: () => void;
  onPlayClick?: () => void;
  isLoading?: boolean;
  title?: string | null;
  notificationId?: number | null;
}

const LevelBonusDetails: React.FC<LevelBonusDetailsProps> = ({
  details,
  title,
  onBack,
  onPlayClick,
  isLoading,
  notificationId,
}) => {
  const { t } = useTranslation();
  const [isFadingOut, setIsFadingOut] = React.useState(false);
  const [isClaiming, setIsClaiming] = React.useState(false);
  const dispatch = useAppDispatch();
  const { refetch } = useBonus();

  const handleBack = () => {
    setIsFadingOut(true);
    setTimeout(() => {
      onBack();
    }, 200);
  };

  const handleClaim = async () => {
    setIsClaiming(true);
    try {
      await refetch();
    } catch (e) {
      // Ignore error
    }
    setIsClaiming(false);
    if (onPlayClick) onPlayClick();
    dispatch(setRewardsOpen(true));
  };

  return (
    <div className={cx(styles.container, { [styles.fadingOut]: isFadingOut })}>
      <button className={styles.backButton} onClick={handleBack} aria-label="Go back">
        <ChevronLeftIcon width="20" height="20" />
      </button>

      <div className={styles.header} style={{ padding: '0 40px' }}>
        <h2>
          {isLoading ? <Skeleton width={180} height={24} /> : title || 'Free Spins Bonus available'}
        </h2>
        {!isLoading && details?.message && (
          <div style={{ marginTop: '8px', color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
            {details.message}
          </div>
        )}
      </div>

      <div className={styles.statsGrid} style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        <div className={styles.statCard}>
          {isLoading || !details ? (
            <Skeleton width={30} height={28} />
          ) : (
            <span className={styles.statValue}>
              {details?.amount || details?.data?.amount
                ? `$${details.amount || details.data.amount}`
                : '-'}
            </span>
          )}
          <span className={styles.statLabel}>{t('notifications.labels.amount', 'Amount')}</span>
        </div>
        <div className={styles.statCard}>
          {isLoading || !details ? (
            <Skeleton width={40} height={28} />
          ) : (
            <span className={styles.statValue}>
              {details?.currencyCode || details?.data?.currencyCode || '-'}
            </span>
          )}
          <span className={styles.statLabel}>{t('notifications.labels.currency', 'Currency')}</span>
        </div>
      </div>

      <div className={styles.cardWrapper} style={{ marginTop: '16px', marginBottom: '16px' }}>
        {(() => {
          const isClaimed = details?.status === 'claimed' || details?.data?.status === 'claimed';
          const isExpired = details?.status === 'expired' || details?.data?.status === 'expired';
          const isDisabled = isLoading || isClaiming || isClaimed || isExpired;

          return (
            <button className={styles.claimButton} onClick={handleClaim} disabled={isDisabled}>
              {isClaiming
                ? t('notifications.actions.loading', 'Loading...')
                : isClaimed
                  ? t('notifications.actions.claimed', 'Claimed')
                  : isExpired
                    ? t('notifications.actions.expired', 'Expired')
                    : t('notifications.actions.claimBonus', 'Claim Bonus')}
            </button>
          );
        })()}
      </div>

      <div className={styles.validUntil}>
        {isLoading || !details ? (
          <Skeleton width={160} height={16} />
        ) : (
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}
          >
            {details.createdAt && (
              <span>
                {t('notifications.labels.availableSince', {
                  date: formatLocalTime(details.createdAt, false),
                })}
              </span>
            )}
            {details.expiresAt && (
              <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                {t('notifications.labels.expires', {
                  date: formatLocalTime(details.expiresAt, false),
                })}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LevelBonusDetails;
